import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import SubscriptionComingSoon from "@/components/SubscriptionComingSoon";
import { SUBSCRIPTION_PRICING_VISIBLE } from "@/lib/featureFlags";
import { TIER_FEATURES, TIER_NOTE, TIER_TAGLINE, tierDisplayName, tierMonthlyCents } from "@/lib/tiers";
import {
  ANNUAL_FRAMING,
  annualPriceFromMonthly,
  formatPriceFromCents,
  monthsFreeFromPrices,
  monthsFreeLabel,
} from "@/lib/pricing";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

// The ladder, keyed on the INTERNAL tier keys so the names, prices and the
// feature bullets all come from `lib/tiers` rather than being typed here.
// Only the CTA and the highlight are local, because those differ per surface.
//
// The bullets and the middle tier's "no AI" note live in the catalogue
// (`TIER_FEATURES` / `TIER_NOTE`) and are shared with the upgrade dialog on
// Settings, so a plan is described the same way whichever door somebody comes
// in by. Two things about them are decisions rather than layout:
//
//  1. **The middle tier's card has to say what it does NOT include.** `studio`
//     is the first paid plan in Hans's history with no AI, which is not what
//     anyone assumes from a $19 upgrade over a free plan that DOES record.
//     A checkmark list can only say what is present, so the absence gets its
//     own line — someone paying $19 expecting AI summaries is a refund and a
//     bad review, and the fix costs one sentence.
//
//  2. **The AI tier is the highlighted one**, not the cheapest paid one. The
//     product is an AI lesson assistant; Studio exists for teachers who
//     deliberately want the platform without it.
const tiers = [
  { key: "basic", period: "forever", cta: "Get started", ctaLink: "/login", highlighted: false },
  { key: "studio", period: "/month", cta: "Start Studio", ctaLink: "/login", highlighted: false },
  { key: "concertmaster", period: "/month", cta: "Start Studio AI", ctaLink: "/login", highlighted: true },
];


const PricingPage = () => (
  <PublicLayout>
    <main className="relative z-10 px-4 md:px-8 pt-12 md:pt-20 pb-20 md:pb-32 max-w-6xl mx-auto safe-area-padding">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="text-center mb-12 md:mb-16"
      >
        <h1
          className="text-3xl md:text-5xl font-light tracking-tight mb-4"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          Simple, <span className="text-[#f4cf08]">transparent</span> pricing
        </h1>
        {/* The standing promise ("start free, upgrade when you're ready") reads
            as a contradiction next to a coming-soon panel, so the placeholder
            state gets its own line. */}
        <p className="text-base md:text-lg text-zinc-400 max-w-lg mx-auto">
          {SUBSCRIPTION_PRICING_VISIBLE
            ? "Start free, upgrade when you're ready. No hidden fees."
            : "Hans is free to start, and we're finalising the rest."}
        </p>
      </motion.div>

      {/* Pricing cards — published. The copy was done at X9 and the Stripe
          prices these link through to now exist, so a visitor who clicks
          through reaches a real checkout. The App Store products (X4) are
          still outstanding, which is why the mobile paywall stays on its
          placeholder; that is a separate constant, not this one. */}
      {!SUBSCRIPTION_PRICING_VISIBLE ? (
        <motion.div {...fadeInUp}>
          <SubscriptionComingSoon variant="page" />
        </motion.div>
      ) : (
      <>
      <motion.div {...fadeInUp} className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {tiers.map((tier) => {
          // Every figure on the card comes off the one declared monthly price:
          // the annual one is ten months of it, and the badge is read back
          // from the two figures being shown rather than from the rule, so it
          // can never describe a saving the card does not display (I10).
          const monthlyCents = tierMonthlyCents(tier.key);
          const annualCents = annualPriceFromMonthly(monthlyCents);
          const monthsFree = monthsFreeLabel(monthsFreeFromPrices(monthlyCents, annualCents));
          return (
          <div
            key={tier.key}
            className={`rounded-2xl p-6 md:p-8 flex flex-col ${
              tier.highlighted
                ? "bg-[#f4cf08]/5 border-2 border-[#f4cf08]/20 relative"
                : "bg-zinc-900/50 border border-white/5"
            }`}
          >
            {tier.highlighted && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#f4cf08] text-black text-[10px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full">
                Most Popular
              </span>
            )}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-white mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                {tierDisplayName(tier.key)}
              </h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-3xl md:text-4xl font-light text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {formatPriceFromCents(monthlyCents)}
                </span>
                {tier.period && <span className="text-sm text-zinc-500">{tier.period}</span>}
              </div>
              <p className="text-sm text-[var(--text-tertiary)]">{TIER_TAGLINE[tier.key]}</p>
              {annualCents && (
                <p className="mt-2 font-mono text-xs text-[var(--text-tertiary)]">
                  {formatPriceFromCents(annualCents)}/year{monthsFree ? ` · ${monthsFree}` : ""}
                </p>
              )}
            </div>

            <ul className="space-y-2.5 mb-8 flex-1">
              {TIER_FEATURES[tier.key].map((f) => (
                <li key={f} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-[#f4cf08]/60 shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-sm text-zinc-300">{f}</span>
                </li>
              ))}
              {TIER_NOTE[tier.key] && (
                <li className="pt-1 text-sm text-[var(--text-tertiary)]">{TIER_NOTE[tier.key]}</li>
              )}
            </ul>

            <Link
              to={tier.ctaLink}
              className={`flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-all ${
                tier.highlighted
                  ? "bg-[#f4cf08] text-black hover:scale-105 active:scale-95 shadow-[0_0_40px_rgba(244,207,8,0.25)]"
                  : "bg-white/5 text-white border border-white/10 hover:bg-white/10"
              }`}
            >
              {tier.cta} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          );
        })}
      </motion.div>

      {/* The convention only sells itself once the free months are placed in
          the year: teaching runs September to June, so the two months nobody
          pays for are the two months nobody teaches. */}
      <motion.p {...fadeInUp} className="mt-8 text-center text-sm text-[var(--text-tertiary)]">
        {ANNUAL_FRAMING}
      </motion.p>
      </>
      )}

    </main>
  </PublicLayout>
);

export default PricingPage;
